// TMOS Notification Banner Component
// Date: November 15, 2025
// Purpose: Display active admin notifications to users

import React, { useState, useEffect } from 'react';
import { XMarkIcon, BellIcon } from '@heroicons/react/24/outline';
import { adminNotificationService, AdminNotification } from '@/services/adminNotificationService';
import { useAuth } from '@/state/auth/AuthProvider';

export const NotificationBanner: React.FC = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (user) {
      loadActiveNotifications();
    }
  }, [user]);

  const loadActiveNotifications = async () => {
    try {
      const data = await adminNotificationService.getActiveNotifications();
      setNotifications(data);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const handleDismiss = async (notificationId: string) => {
    try {
      await adminNotificationService.dismissNotification(notificationId);
      setDismissedIds(new Set([...dismissedIds, notificationId]));
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  };

  const handleView = async (notification: AdminNotification) => {
    try {
      await adminNotificationService.recordView(notification.id);
    } catch (error) {
      console.error('Error recording view:', error);
    }
  };

  // Filter out dismissed notifications
  const visibleNotifications = notifications.filter(n => !dismissedIds.has(n.id));

  if (visibleNotifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 space-y-2 p-4">
      {visibleNotifications.map((notification) => (
        <div
          key={notification.id}
          className="max-w-4xl mx-auto bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] rounded-lg shadow-lg p-4 flex items-start gap-4"
          onClick={() => handleView(notification)}
        >
          <BellIcon className="w-6 h-6 flex-shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="font-semibold mb-1">{notification.title}</h3>
            <p className="text-sm whitespace-pre-wrap">{notification.message}</p>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDismiss(notification.id);
            }}
            className="flex-shrink-0 text-[var(--color-primary-text)] hover:opacity-75"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      ))}
    </div>
  );
};


